const groupPrefix = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.DISCOUNT_CODES)

export const apiDiscountCodes = {
	fetchDiscountCodes: async ({
		page = 1,
		perPage = 25,
		workflowStatus,
		discountType,
		search,
		dateRange,
		archive,
		campaignId,
		createdByEmployeeId,
		requesterName,
		isInfluencer,
		isB2b,
	}: {
		page?: number
		perPage?: number
		workflowStatus?: ECodeStatus[]
		discountType?: string[]
		search?: string
		dateRange?: string
		archive?: boolean
		campaignId?: number | number[]
		createdByEmployeeId?: number | number[]
		requesterName?: string
		isInfluencer?: boolean
		isB2b?: boolean
	} = {}): Promise<IApiResponseModel<IDiscountCodeListModel[]>> => {
		const { data, meta } = await apiCommon<IDiscountCodeListItem[]>(`${groupPrefix}`, {
			method: 'GET',
			...createQuery(buildPagination(page, perPage), {
				search,
				archive,
				workflow_status: workflowStatus,
				discount_type: discountType,
				date_range: dateRange,
				campaign_id: campaignId,
				created_by_employee_id: createdByEmployeeId,
				requester_name: requesterName,
				is_influencer: isInfluencer,
				is_b2b: isB2b,
			}),
		})

		return { data: data.map(DiscountCodeListItem), meta }
	},

	fetchDiscountCode: async (
		id: number,
	): Promise<{
		formData: IDiscountCodeFormData
		coupons: IDiscountCodeCouponDto[]
		ownerName: string | null
		createdByEmployeeId: number | null
	}> => {
		const { data } = await apiCommon<IDiscountCodeDetailDto>(`${groupPrefix}/${id}`, { method: 'GET' })
		const emp = data.created_by
		const ownerName = emp ? `${emp.first_name ?? ''} ${emp.last_name ?? ''}`.trim() || emp.email : null

		return {
			ownerName,
			createdByEmployeeId: emp?.id ?? null,
			formData: DiscountCodeDetailToInitialValues(data),
			coupons: data.coupons ?? [],
		}
	},

	fetchCodeOptions: async (): Promise<ICodeOptionsModel> => {
		const { data } = await apiCommon<ICodeOptionsDto>(`${groupPrefix}/form-options`, { method: 'GET' })

		return CodeOptions(data)
	},

	fetchRecipientSegmentClients: async (
		segment: string,
		{ page = 1, perPage = 25 }: { page?: number; perPage?: number } = {},
	): Promise<IApiResponseModel<IDiscountCodeRecipientSegmentClientModel[]>> => {
		const { data, meta } = await apiCommon<IDiscountCodeRecipientSegmentClientDto[]>(
			`${groupPrefix}/recipient-segment-clients`,
			{
				method: 'GET',
				...createQuery(buildPagination(page, perPage), null, undefined, { segment }),
			},
		)

		return { data: data.map(RecipientSegmentClient), meta }
	},

	createDiscountCode: async (payload: IDiscountCodeCreatePayload): Promise<IDiscountCodeListModel> => {
		const { data } = await apiCommon<IDiscountCodeListItem>(`${groupPrefix}`, {
			method: 'POST',
			body: payload,
		})

		return DiscountCodeListItem(data)
	},

	updateDiscountCode: async (id: number, payload: IDiscountCodeCreatePayload) => {
		await apiCommon(`${groupPrefix}/${id}`, {
			method: 'PATCH',
			body: payload,
		})
	},

	archiveDiscountCode: async (id: number) => {
		await apiCommon(`${groupPrefix}/${id}/archive`, { method: 'POST' })
	},

	restoreDiscountCode: async (id: number, targetStatus: 'active' | 'draft' = 'draft') => {
		await apiCommon(`${groupPrefix}/${id}/restore`, { method: 'POST', body: { target_status: targetStatus } })
	},

	updateManyDiscountCodes: async (payload: IDiscountCodeUpdateManyPayload): Promise<IBulkUpdateResult> => {
		const { data } = await apiCommon<IBulkUpdateResult>(`${groupPrefix}/update-many`, {
			method: 'PUT',
			body: payload,
		})
		return data
	},

	updateManyDiscountCodesCampaign: async (
		payload: IDiscountCodeUpdateManyCampaignPayload,
	): Promise<IBulkUpdateResult> => {
		const { data } = await apiCommon<IBulkUpdateResult>(`${groupPrefix}/update-many-campaign`, {
			method: 'PUT',
			body: payload,
		})
		return data
	},

	fetchDiscountCodeActivityLogs: async (
		id: number,
		{ page = 1, perPage = DISCOUNT_CODE_HISTORY_PER_PAGE }: { page?: number; perPage?: number } = {},
	): Promise<IApiResponseModel<IAuditLogEntryModel[]>> => {
		// Stronicowana lista zagnieżdżona w polu data; endpoint przyjmuje page i per_page.
		const { data } = await apiCommon<IActivityLogResponseDto>(`${groupPrefix}/${id}/activity-logs`, {
			method: 'GET',
			...createQuery(null, null, undefined, { page, per_page: perPage }),
		})
		return ActivityLogResponse(data)
	},
}
